import React from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import EditableImage from "../../landingEditor/EditableImage";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { getValueAtDescriptorPath } from "../elementEditing";

function isPrimitiveCollection(property) {
  return Boolean(property?.item?.type) && !Array.isArray(property?.item?.fields);
}

function createCollectionItem(property) {
  if (isPrimitiveCollection(property)) return "";
  const next = {};
  (property?.item?.fields || []).forEach((field) => {
    if (!field?.key) return;
    if (field.key === "id") next[field.key] = `item-${Date.now().toString(36)}`;
    else if (/position/i.test(field.key)) next[field.key] = field.defaultValue || "center";
    else if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) next[field.key] = field.defaultValue ?? true;
    else next[field.key] = field.defaultValue ?? "";
  });
  return next;
}

function CollectionField({ property, index, field, form, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const path = `${property.path}.${index}.${field.key}`;
  const value = getValueAtDescriptorPath(form, path, "");
  if (field.key === "id") return null;
  if (field.type === SEGMENT_FIELD_TYPES.IMAGE) {
    return (
      <EditableImage
        value={value}
        label={field.label || "Imagen"}
        disabled={disabled}
        onUpload={(file, metadata) => onUploadImage?.(path, file, metadata)}
        onRemove={() => onRemoveImage?.(path)}
      />
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{field.label || field.key}</InputLabel>
        <Select label={field.label || field.key} value={value ?? ""} onChange={(event) => onCommit?.(path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  return (
    <TextField
      fullWidth
      size="small"
      label={field.label || field.key}
      value={value ?? ""}
      disabled={disabled}
      multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA}
      minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined}
      inputProps={{ maxLength: field.maxLength }}
      onChange={(event) => onCommit?.(path, event.target.value)}
    />
  );
}

export default function CollectionItemEditor({ descriptor, form, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const collection = descriptor?.collection;
  const property = descriptor?.property || collection?.property;
  if (!property) return null;
  const itemsValue = getValueAtDescriptorPath(form, property.path, []);
  const items = Array.isArray(itemsValue) ? itemsValue : [];
  const maxItems = Number.isFinite(property?.limits?.maxItems) ? property.limits.maxItems : 40;
  const manager = descriptor?.kind === "collection-manager";
  const selectedIndex = Number.isInteger(collection?.index) ? collection.index : 0;
  const primitive = isPrimitiveCollection(property);

  const updateItems = (next) => onCommit?.(property.path, next.slice(0, maxItems));
  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    updateItems(next);
  };
  const remove = (index) => updateItems(items.filter((_, itemIndex) => itemIndex !== index));
  const add = () => {
    if (items.length >= maxItems) return;
    updateItems([...items, createCollectionItem(property)]);
  };

  if (!manager) {
    const fields = property.item?.fields || [];
    return (
      <Stack spacing={1.2}>
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
          Editás este elemento dentro de {property.label || "la colección"}.
        </Typography>
        {primitive ? (
          <TextField
            fullWidth
            size="small"
            label={property.label || "Texto"}
            value={items[selectedIndex] ?? ""}
            disabled={disabled}
            onChange={(event) => {
              const next = [...items];
              next[selectedIndex] = event.target.value;
              updateItems(next);
            }}
          />
        ) : fields.map((field) => (
          <CollectionField
            key={`${property.path}:${selectedIndex}:${field.key}`}
            property={property}
            index={selectedIndex}
            field={field}
            form={form}
            disabled={disabled}
            onCommit={onCommit}
            onUploadImage={onUploadImage}
            onRemoveImage={onRemoveImage}
          />
        ))}
        <Stack direction="row" spacing={0.5}>
          <IconButton aria-label="Mover arriba" disabled={disabled || selectedIndex <= 0} onClick={() => move(selectedIndex, -1)}><KeyboardArrowUpRoundedIcon /></IconButton>
          <IconButton aria-label="Mover abajo" disabled={disabled || selectedIndex >= items.length - 1} onClick={() => move(selectedIndex, 1)}><KeyboardArrowDownRoundedIcon /></IconButton>
          <Button color="error" disabled={disabled} startIcon={<DeleteOutlineRoundedIcon />} onClick={() => remove(selectedIndex)} sx={{ textTransform: "none" }}>Quitar</Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography sx={{ fontWeight: 950 }}>{property.label || descriptor.label || "Elementos"}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>{items.length} / {maxItems}</Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} disabled={disabled || items.length >= maxItems} onClick={add} sx={{ textTransform: "none", borderRadius: 999 }}>Agregar</Button>
      </Stack>

      {!items.length ? <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>Todavía no hay elementos.</Typography> : null}

      <Stack spacing={1}>
        {items.map((item, index) => (
          <Box key={item?.id || `${property.path}:${index}`} sx={{ p: 1.2, border: "1px solid rgba(25,36,47,.1)", borderRadius: 2.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 900 }}>Elemento {index + 1}</Typography>
              <Stack direction="row" spacing={0.2}>
                <IconButton size="small" aria-label={`Mover elemento ${index + 1} arriba`} disabled={disabled || index === 0} onClick={() => move(index, -1)}><KeyboardArrowUpRoundedIcon fontSize="small" /></IconButton>
                <IconButton size="small" aria-label={`Mover elemento ${index + 1} abajo`} disabled={disabled || index === items.length - 1} onClick={() => move(index, 1)}><KeyboardArrowDownRoundedIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" aria-label={`Quitar elemento ${index + 1}`} disabled={disabled} onClick={() => remove(index)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
              </Stack>
            </Stack>
            {primitive ? (
              <TextField
                fullWidth
                size="small"
                label={property.label || "Texto"}
                value={String(item ?? "")}
                disabled={disabled}
                inputProps={{ maxLength: property.item?.maxLength }}
                onChange={(event) => {
                  const next = [...items];
                  next[index] = event.target.value;
                  updateItems(next);
                }}
              />
            ) : (
              <Stack spacing={1}>
                {(property.item?.fields || []).map((field) => (
                  <CollectionField
                    key={`${property.path}:${index}:${field.key}`}
                    property={property}
                    index={index}
                    field={field}
                    form={form}
                    disabled={disabled}
                    onCommit={onCommit}
                    onUploadImage={onUploadImage}
                    onRemoveImage={onRemoveImage}
                  />
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
